import { useMemo } from 'react';
import { assert } from '@chainflip/utils/assertion';
import {
  type AnyChainflipAsset,
  assetConstants,
  type BaseChainflipAsset,
} from '@chainflip/utils/chainflip';
import BigNumber from 'bignumber.js';
import { getPoolSwapsQuery } from '@/shared/graphql/explorer/pools';
import {
  type SwapFeeType,
  type GetPoolLastPricesQuery,
} from '@/shared/graphql/generated/explorer/graphql';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { formatWithNumeral } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';

type Swap = NonNullable<
  GetPoolLastPricesQuery['earliestSwap'] | GetPoolLastPricesQuery['latestSwap']
>['nodes'][number];

type Fee = {
  amount: string;
  asset: AnyChainflipAsset;
  type: SwapFeeType;
};

const deductedFeeTypes = ['NETWORK', 'BROKER'] as const;
type SwapDeductedFeeType = (typeof deductedFeeTypes)[number];

export const aggregatedUsdcSwapDeductedFees = (fees: Fee[]) =>
  deductedFeeTypes.reduce(
    (acc, type) => {
      acc[type as SwapDeductedFeeType] = {
        amount: fees
          .filter((fee) => fee.type === type && fee.asset === 'Usdc')
          .reduce((sum, fee) => sum.plus(fee.amount), new BigNumber(0)),
      };
      return acc;
    },
    {} as Record<SwapDeductedFeeType, { amount: BigNumber }>,
  );

export const calculateUsdcPrice = (swap: Swap, baseAsset: BaseChainflipAsset): number => {
  let price;
  let fromAsset;
  let fromAmount;
  let toAmount;

  assert([swap.sourceAsset, swap.destinationAsset].includes(baseAsset), 'Invalid swap');

  const aggregates = aggregatedUsdcSwapDeductedFees(swap.swapFeesBySwapId?.nodes ?? []);

  if (swap.sourceAsset === 'Usdc' || swap.destinationAsset === 'Usdc') {
    fromAsset = swap.sourceAsset;
    fromAmount = swap.swapInputAmount;
    toAmount = swap.swapOutputAmount;
  } else {
    fromAsset = swap.sourceAsset === baseAsset ? swap.sourceAsset : 'Usdc';
    fromAmount = swap.sourceAsset === baseAsset ? swap.swapInputAmount : swap.intermediateAmount;

    const intermediateAmountForLeg1 =
      swap.sourceAsset === baseAsset // add fees to the intermediateAmount for the first leg swap
        ? new BigNumber(swap.intermediateAmount ?? 0)
            .plus(aggregates.NETWORK.amount)
            .plus(aggregates.BROKER.amount)
            .toString()
        : swap.intermediateAmount;

    toAmount =
      swap.destinationAsset === baseAsset ? swap.swapOutputAmount : intermediateAmountForLeg1;
  }

  assert(fromAmount && toAmount, 'Invalid swap');

  if (fromAsset === 'Usdc') {
    price = new BigNumber(fromAmount).dividedBy(toAmount);
  } else {
    price = new BigNumber(toAmount).dividedBy(fromAmount);
  }

  return Number(
    price
      .shiftedBy(assetConstants[baseAsset].decimals - assetConstants.Usdc.decimals)
      .toFixed(assetConstants.Usdc.decimals),
  );
};

export const useComparePoolPrice = ({
  baseAsset,
  endDate,
  startDate,
}: {
  baseAsset: BaseChainflipAsset;
  endDate: Date;
  startDate: Date;
}) => {
  const { data, isLoading } = useGqlQuery(getPoolSwapsQuery, {
    variables: {
      baseAsset,
      latestSwapTimestamp: endDate.toISOString(),
      earliestSwapTimestamp: startDate.toISOString(),
    },
    placeholderData: (previousData) => previousData,
    enabled: baseAsset !== undefined,
  });

  return useMemo(() => {
    const latestSwap = data?.latestSwap?.nodes.at(0);
    const earliestSwap = data?.earliestSwap?.nodes.at(0);

    const latestPrice = latestSwap ? calculateUsdcPrice(latestSwap, baseAsset) : undefined;
    const earliestPrice = earliestSwap ? calculateUsdcPrice(earliestSwap, baseAsset) : undefined;

    return {
      latestPrice,
      earliestPrice,
      diff:
        latestPrice && earliestPrice ? new BigNumber(latestPrice).minus(earliestPrice) : undefined,
      percentDiff:
        latestPrice && earliestPrice
          ? formatWithNumeral(
              TokenAmount.fromWholeUnits(latestPrice, 2)
                .difference(TokenAmount.fromWholeUnits(earliestPrice, 2))
                .toFixed(2),
            )
          : undefined,
      isLoading,
    };
  }, [data, isLoading]);
};
